import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import DoughnutChart from 'src/components/followUp/common/BaseWidgets/DoughnutChart/DoughnutChart';
import type { Aggregate } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useExportOptions } from './hooks';

const QuestionWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { t } = useTranslation(['common', 'followUp']);
  const { colors } = useTheme();

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.QUESTION,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, queryFilters, comparisonData } =
    useWidgetQuery<Aggregate>(endpoint);

  const labels = [
    {
      name: t('common:yes'),
      color: colors.secondary[500],
      comparisonColor: comparisonData && colors.comparison[400],
    },
    {
      name: t('common:no'),
      color: colors.neutral[100],
      comparisonColor: comparisonData && colors.comparison[50],
    },
  ];

  const exportOptions = useExportOptions({
    data: data ?? null,
    comparisonData: comparisonData ?? null,
    labels,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      error={errorMsg}
      refetch={refetch}
      loading={isFetching}
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        data?.totalObjects === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : (
          <WidgetFooterLegend labels={labels} />
        )
      }
    >
      <DoughnutChart
        data={
          data
            ? [
                {
                  label: t('common:yes'),
                  absoluteValue: data.value,
                  relativeValue: getRelativeValue(
                    data.value,
                    data.totalObjects,
                  ),
                },
                {
                  label: t('common:no'),
                  absoluteValue: data.totalObjects - data.value,
                  relativeValue: getRelativeValue(
                    data.totalObjects - data.value,
                    data.totalObjects,
                  ),
                },
              ]
            : []
        }
        comparisonData={
          comparisonData
            ? [
                {
                  label: t('common:yes'),
                  absoluteValue: comparisonData.value,
                  relativeValue: getRelativeValue(
                    comparisonData.value,
                    comparisonData.totalObjects,
                  ),
                },
                {
                  label: t('common:no'),
                  absoluteValue:
                    comparisonData.totalObjects - comparisonData.value,
                  relativeValue: getRelativeValue(
                    comparisonData.totalObjects - comparisonData.value,
                    comparisonData.totalObjects,
                  ),
                },
              ]
            : []
        }
        labels={labels}
      />
    </WidgetFrame>
  );
};

export default QuestionWidget;
